import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  FileText, Plus, Loader2, Eye, Download, Trash2, Search, HardHat,
} from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  job_title: '', site_address: '', city: '', postcode: '',
  client_name: '', client_phone: '', start_date: '', days: 1,
  scope: '', supervisor: 'Matt Lowther', p1: '', p2: '', p3: '', notes: '',
};

export const AdhocRams = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/adhoc-rams', { params: { q: search } });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load ad-hoc RAMS');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCreate = async () => {
    if (!form.job_title.trim()) { toast.error('Job title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        job_title: form.job_title, site_address: form.site_address,
        city: form.city, postcode: form.postcode,
        client_name: form.client_name, client_phone: form.client_phone,
        start_date: form.start_date, days: Number(form.days) || 1,
        scope: form.scope, supervisor: form.supervisor,
        personnel: [form.p1, form.p2, form.p3].filter((p) => p && p.trim()),
        notes: form.notes,
      };
      await api.post('/adhoc-rams', payload);
      toast.success('RAMS generated');
      setDialogOpen(false);
      setForm(EMPTY);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'RAMS generation failed');
    } finally {
      setSaving(false);
    }
  };

  const openBlob = async (item, download) => {
    setBusyId(item.id + (download ? '-dl' : '-view'));
    try {
      const { data } = await api.get(`/adhoc-rams/${item.id}/download`, {
        params: download ? { download: true } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url; a.download = item.filename || 'RAMS.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error('Could not open file');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.job_title}" RAMS?`)) return;
    setBusyId(item.id + '-del');
    try {
      await api.delete(`/adhoc-rams/${item.id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="adhoc-rams-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <HardHat className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Ad-hoc RAMS</h1>
              <p className="text-slate-500 text-sm">Generate RAMS documents for one-off, non-customer jobs</p>
            </div>
          </div>
          <Button
            onClick={() => { setForm(EMPTY); setDialogOpen(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="new-adhoc-rams-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Ad-hoc RAMS
          </Button>
        </div>

        <Card>
          <CardContent className="py-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by job, address or client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="adhoc-rams-search"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-slate-400">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">No ad-hoc RAMS yet. Click "New Ad-hoc RAMS" to create one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Start</TableHead>
                    <TableHead>Days</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} data-testid={`adhoc-rams-row-${it.id}`}>
                      <TableCell className="font-medium text-slate-900">{it.job_title}</TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {[it.site_address, it.city, it.postcode].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{it.client_name || '—'}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{it.start_date ? formatDate(it.start_date) : '—'}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{it.days}</TableCell>
                      <TableCell className="text-slate-500 text-xs">{it.created_at ? formatDateTime(it.created_at) : ''}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openBlob(it, false)} disabled={busyId === it.id + '-view'} data-testid={`view-adhoc-${it.id}`} title="View">
                            {busyId === it.id + '-view' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openBlob(it, true)} disabled={busyId === it.id + '-dl'} data-testid={`download-adhoc-${it.id}`} title="Download">
                            {busyId === it.id + '-dl' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(it)} disabled={busyId === it.id + '-del'} className="text-red-600 hover:text-red-700" data-testid={`delete-adhoc-${it.id}`} title="Delete">
                            {busyId === it.id + '-del' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Ad-hoc RAMS</DialogTitle>
              <DialogDescription>Fill in the job details — the RAMS PDF is generated instantly and stored here.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <div className="sm:col-span-2">
                <Label>Job / Project Title *</Label>
                <Input value={form.job_title} onChange={(e) => setField('job_title', e.target.value)} placeholder="e.g. Scaffold Inspection - Unit 4" data-testid="adhoc-job-title" />
              </div>
              <div className="sm:col-span-2">
                <Label>Site Address</Label>
                <Input value={form.site_address} onChange={(e) => setField('site_address', e.target.value)} placeholder="Street / site line" data-testid="adhoc-site-address" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={form.city} onChange={(e) => setField('city', e.target.value)} data-testid="adhoc-city" />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input value={form.postcode} onChange={(e) => setField('postcode', e.target.value)} data-testid="adhoc-postcode" />
              </div>
              <div>
                <Label>Client Name</Label>
                <Input value={form.client_name} onChange={(e) => setField('client_name', e.target.value)} data-testid="adhoc-client-name" />
              </div>
              <div>
                <Label>Client Phone</Label>
                <Input value={form.client_phone} onChange={(e) => setField('client_phone', e.target.value)} data-testid="adhoc-client-phone" />
              </div>
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} data-testid="adhoc-start-date" />
              </div>
              <div>
                <Label>Number of Days</Label>
                <Input type="number" min={1} value={form.days} onChange={(e) => setField('days', e.target.value)} data-testid="adhoc-days" />
              </div>
              <div className="sm:col-span-2">
                <Label>Scope of Works</Label>
                <Textarea rows={3} value={form.scope} onChange={(e) => setField('scope', e.target.value)} placeholder="Describe the work to be carried out..." data-testid="adhoc-scope" />
              </div>
              <div className="sm:col-span-2">
                <Label>Works Supervisor</Label>
                <Input value={form.supervisor} onChange={(e) => setField('supervisor', e.target.value)} data-testid="adhoc-supervisor" />
                <p className="text-xs text-slate-400 mt-1">Supervisor phone is fixed: 07773270590/01908 348250</p>
              </div>
              <div>
                <Label>Personnel 1</Label>
                <Input value={form.p1} onChange={(e) => setField('p1', e.target.value)} data-testid="adhoc-p1" />
              </div>
              <div>
                <Label>Personnel 2</Label>
                <Input value={form.p2} onChange={(e) => setField('p2', e.target.value)} data-testid="adhoc-p2" />
              </div>
              <div>
                <Label>Personnel 3</Label>
                <Input value={form.p3} onChange={(e) => setField('p3', e.target.value)} data-testid="adhoc-p3" />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes (internal)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="adhoc-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="adhoc-generate-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Generate RAMS
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdhocRams;
